import React, { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  selectPostingUserScreenFromState,
  selectPostingUserScreenTopLevelErrorFromState,
  selectScreeningStatusFromState,
  selectVirtualCard,
  selectVirtualCardIdFromRoute,
} from 'src/app/ScreenVirtualCard/redux/screenVirtualCard.selectors'
import SanctionedScreeningForm, {
  AllScreeningFields,
  RegisterAccountCountryFields,
} from 'src/component-form/SanctionedScreeningForm/SanctionedScreeningForm'
import AddressSuggestionModal from 'src/component-modal/AddressSuggestionModal/AddressSuggestionModal'
import ScreeningSuccessModal from 'src/component-modal/ScreeningSuccessModal/ScreeningSuccessModal'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import Popup from 'src/components/Popup/Popup'
import { useFlyoutDispatch } from 'src/hooks'
import { useAddressValidation } from 'src/hooks/useAddressValidation'
import { getLocaleInfo } from 'src/localization/locales'
import { ScreeningStatus } from 'src/models/virtualCard.models'
import { closeModal } from 'src/navigation/Lnk'
import { getCountrys } from 'src/redux/actions/data/countrys.actions'
import { postUserScreen } from 'src/redux/actions/data/users.actions'
import {
  getVirtualCard,
  getVirtualCardPermissions,
} from 'src/redux/actions/data/virtualCards.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCountryListModelsFromState,
  selectCountryOptions,
} from 'src/redux/selectors/data/countrys.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

export default function ScreenVirtualCard(): ReactElement | null {
  const dispatch = useFlyoutDispatch()
  const appName = useSelector(selectAppName)
  const rawCountryOptions = useSelector(selectCountryOptions)
  const countrysMap = useSelector(selectCountryListModelsFromState)
  const virtualCardId = useSelector(selectVirtualCardIdFromRoute)
  const virtualCard = useSelector(selectVirtualCard)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const showAddressSuggestions =
    useSelector(selectFeatureFlaggerFromState).show_address_suggestions ?? false

  const {
    validateBeforeSubmit,
    showSuggestionModal,
    suggestions,
    isConfirmation,
    onSuggestionSelect,
    onSuggestionClose,
    validationError,
    isValidating,
  } = useAddressValidation()

  const countryOptions = useMemo(
    () =>
      rawCountryOptions.map((c) => ({
        value: c.value,
        label: c.details.text,
      })),
    [rawCountryOptions],
  )

  useEffect((): void => {
    dispatch(getCountrys())
  }, [])

  useEffect((): void => {
    dispatch(getVirtualCard(virtualCardId))
    dispatch(getVirtualCardPermissions(virtualCardId))
  }, [virtualCard?.status, virtualCardId])

  const handleBeforeReview = useCallback(
    async (
      addressFields: RegisterAccountCountryFields,
    ): Promise<RegisterAccountCountryFields | null> => {
      return new Promise((resolve) => {
        validateBeforeSubmit(
          {
            line1: addressFields.address1 || '',
            line2: addressFields.address2,
            city: addressFields.city || '',
            province: addressFields.province,
            postal: addressFields.postal,
            country: addressFields.country || '',
          },
          (confirmedSuggestion) => {
            if (confirmedSuggestion) {
              resolve({
                address1: confirmedSuggestion.line1,
                address2: confirmedSuggestion.line2 ?? '',
                city: confirmedSuggestion.city ?? '',
                province: confirmedSuggestion.province ?? '',
                postal: confirmedSuggestion.postal ?? '',
                country: addressFields.country,
              })
            } else {
              resolve(addressFields)
            }
          },
          () => resolve(null),
        )
      })
    },
    [validateBeforeSubmit],
  )

  const onScreeningFormSubmit = (body: AllScreeningFields): void => {
    const splitBirthday = body.birthday?.split('/')
    dispatch(
      postUserScreen(
        {
          ...body,
          birthday: splitBirthday
            ? getLocaleInfo(locale).dateString(splitBirthday)
            : undefined,
          virtualCardId: virtualCard?.id,
        },
        virtualCardId,
      ),
    )
  }

  const postingUserScreen = useSelector(selectPostingUserScreenFromState)
  const postingUserScreenTopLevelError = useSelector(
    selectPostingUserScreenTopLevelErrorFromState,
  )
  const screeningStatus = useSelector(selectScreeningStatusFromState)

  if (virtualCard === undefined || virtualCard.issuer === undefined) {
    return (
      <Popup>
        <NotFoundPopupContent />
      </Popup>
    )
  }

  const needsScreening =
    screeningStatus !== ScreeningStatus.Pass &&
    virtualCard.screeningRequired &&
    virtualCard.screening?.status === ScreeningStatus.NeedsMoreInfo

  if (needsScreening) {
    return (
      <Popup hideExit>
        <SanctionedScreeningForm
          appName={<>{appName}</>}
          issuerName={virtualCard.issuer.name}
          onSubmit={onScreeningFormSubmit}
          onBeforeReview={showAddressSuggestions ? handleBeforeReview : undefined}
          countryOptions={countryOptions}
          countrysMap={countrysMap}
          isLoading={postingUserScreen || (showAddressSuggestions && isValidating)}
          topLevelError={postingUserScreenTopLevelError || (showAddressSuggestions ? validationError : undefined)}
          locale={locale}
        />
        {showAddressSuggestions && (
          <AddressSuggestionModal
            isOpen={showSuggestionModal}
            suggestions={suggestions}
            isConfirmation={isConfirmation}
            onSelect={onSuggestionSelect}
            onClose={onSuggestionClose}
            countryLabel={
              suggestions.length > 0
                ? countryOptions.find(
                    (c) => c.value === suggestions[0].country,
                  )?.label
                : undefined
            }
          />
        )}
      </Popup>
    )
  }

  if (screeningStatus === ScreeningStatus.Pass) {
    return (
      <ScreeningSuccessModal
        onClick={() => dispatch(closeModal())}
        appName={appName}
      />
    )
  }

  return null
}
